import { StanButton, StanInput } from '@/components';
import IconLock from '@/assets/icons/ic-lock.svg';
import Logo from '@/assets/images/stan-logo-text.png';
import { useStanRouter } from '@/hooks/useStanRouter';
import { ResetPasswordFormState } from '@/features/auth/types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema } from '@/features/auth/validations';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { JWTData } from '@/types/utils';
import { parseJWT } from '@/lib/helpers';
import { useLoginMutation, useResetPasswordMutation } from '@/features/auth/queries';
import { stanNotify } from '@/lib';
import { useAuthStore } from '@/stores';

interface Quote {
    quote: string;
    author: string;
}

const randomFunQuotes: Quote[] = [
    {
        quote: 'A password reset a day keeps the doctor away',
        author: 'Benjamin Franklin',
    },
    {
        quote: 'I can never remember the password I just reset',
        author: 'George Washington',
    },
    {
        quote: 'Four score and seven passwords ago',
        author: 'Abe Lincoln',
    },
    {
        quote: 'Live, laugh, reset your passwords',
        author: 'Madonna',
    },
];

export default function ResetPasswordPage() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<ResetPasswordFormState>({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    const [searchParams] = useSearchParams();
    const resetToken = searchParams.get('token') ?? '';
    const resetTokenData: JWTData | undefined = useMemo(() => parseJWT(resetToken), [resetToken]);

    const password = watch('password');
    const confirmPassword = watch('confirmPassword');
    const quote = useRef<Quote>(randomFunQuotes[Math.floor(Math.random() * randomFunQuotes.length)] as Quote);
    const isTokenExpired =
        !resetToken || !resetTokenData.payload?.exp || Math.floor(Date.now() / 1000) >= resetTokenData.payload.exp;
    const { mutateAsync: resetPasswordAsyncMutate, isPending: isLoading } = useResetPasswordMutation();
    const { mutateAsync: loginAsyncMutate } = useLoginMutation();
    const setLoginApiData = useAuthStore(state => state.setLoginApiData);
    const { push } = useStanRouter();

    const handleResetPassword = async (data: ResetPasswordFormState) => {
        try {
            const { email } = await resetPasswordAsyncMutate({
                token: resetToken,
                password: data.password,
                confirmPassword: data.confirmPassword,
            });
            if (!email) throw new Error('Email not found');

            stanNotify({
                type: 'success',
                title: 'Password Reset',
                text: 'Your password has been reset.',
                duration: 2000,
            });
            const loginData = await loginAsyncMutate({ email_or_username: email, password: data.password });
            setLoginApiData(loginData);
            push('/dashboard');
        } catch (error) {
            stanNotify({
                type: 'error',
                title: 'Oops.',
                text: 'Something went wrong. Please try again later.',
                duration: 2000,
                error: {
                    message: error instanceof Error ? error.message : 'Something went wrong. Please try again later.',
                },
            });
            push('/forgot_password');
        }
    };

    // Handle token expiration
    useEffect(() => {
        if (isTokenExpired) {
            stanNotify({
                type: 'error',
                title: 'Reset Password Link Expired',
                text: 'Please send another link',
                duration: 5000,
            });
            push('/forgot_password');
        }
    }, [isTokenExpired]);

    return (
        <div className='w-full max-w-md space-y-8 p-8'>
            <div className='flex flex-col items-center gap-2'>
                <img className='mx-auto mb-6' src={Logo} height={100} width={100} alt='Stan Logo' />
                <p className='text-heading-heading2 text-center'>Set New Password</p>
                <p className='text-body-2-regular text-center'>&quot;{quote.current.quote}&quot;</p>
                <p className='text-body-2-regular text-center'>-{quote.current.author}</p>
            </div>

            <div className='mt-8 space-y-6'>
                <form className='space-y-4' onSubmit={handleSubmit(handleResetPassword)}>
                    <StanInput
                        id='password'
                        type='password'
                        placeholder='Password'
                        autoComplete='current-password'
                        disabled={isLoading}
                        hintText={errors.password?.message}
                        error={!!errors.password}
                        startIcon={IconLock}
                        value={password}
                        {...register('password')}
                    />

                    <StanInput
                        id='confirmPassword'
                        type='password'
                        placeholder='Re-enter Password'
                        autoComplete='current-password'
                        disabled={isLoading}
                        hintText={errors.confirmPassword?.message}
                        error={!!errors.confirmPassword}
                        startIcon={IconLock}
                        value={confirmPassword}
                        {...register('confirmPassword')}
                    />

                    <div className='mb-12 flex flex-col items-center justify-between gap-2'>
                        <StanButton
                            type='submit'
                            color='primary'
                            loading={isLoading}
                            disabled={isLoading}
                            className='w-full'
                            name='sign-in'
                        >
                            Reset Password
                        </StanButton>

                        <div className='text-body-2-regular'>
                            Go back to{' '}
                            <span
                                className='link-text text-body-2-bold'
                                onClick={e => {
                                    e.preventDefault();
                                    push('/login');
                                }}
                            >
                                Login
                            </span>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
